import React, { useState } from 'react';
import {
    Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  // Divider,
  Chip,
  useTheme,
  alpha,
  IconButton,
  // Tooltip,
  Dialog,
  // DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  // Alert,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
  Diamond as DiamondIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  TrendingUp as ChartIcon,
  // People as PeopleIcon,
  Shield as SecurityIcon,
  // Help as HelpIcon,
  CreditCard as CreditCardIcon,
  // WorkspacePremium as PremiumIcon,
  Celebration as CelebrationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
// import { useNavigate } from 'react-router-dom';

const SubscriptionPage = () => {
  const theme = useTheme();
  // const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    upiId: '',
    transactionId: ''
  });
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const isDarkMode = theme.palette.mode === 'dark';

  // Glassmorphism styles
  const glassStyles = {
    background: isDarkMode
      ? 'rgba(30, 41, 59, 0.7)'
      : 'linear-gradient(135deg,rgb(232, 234, 239) 0%,rgb(219, 217, 247) 100%)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: `1px solid ${isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(99, 102, 241, 0.1)'}`,
    boxShadow: isDarkMode
      ? '0 8px 32px rgba(0, 0, 0, 0.2)'
      : '0 8px 32px rgba(37, 99, 235, 0.08)',
  };

  // Solid colors for alpha() function (not gradients)
  const planColors = {
    basic: '#2563EB',
    standard: '#8B5CF6',
    premium: '#059669',
    light: '#3B82F6'
  };

  // Gradient strings for background (not for alpha())
  const gradientStyles = {
    primary: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
    premium: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
    enterprise: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    light: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
  };

  // Plans data
  const plans = [
    {
      id: 'basic',
      name: 'Essential',
      tagline: 'Perfect for Single Module Operations',
      icon: <ReceiptIcon />,
      price: '₹15,000',
      amc: '₹5,000/year',
      description: 'Get started with either billing or inventory management',
      gradient: gradientStyles.primary,
      solidColor: planColors.basic,
      features: [
        'Billing OR Inventory Module (Choose One)',
        'Basic Customer Management',
        'Invoice Generation',
        'Basic Reports',
        'Single User Access',
        'Email Support',
        'Data Backup (Monthly)',
        'Basic Dashboard',
      ],
      highlight: false,
      ctaText: 'Get Essential',
      recommended: false
    },
    {
      id: 'standard',
      name: 'Professional',
      tagline: 'Complete Business Management Solution',
      icon: <InventoryIcon />,
      price: '₹20,000',
      amc: '₹7,000/year',
      description: 'Comprehensive solution for billing and inventory',
      gradient: gradientStyles.premium,
      solidColor: planColors.standard,
      features: [
        'Billing + Inventory Both Modules',
        'Advanced Customer/Supplier Management',
        'Multi-user Access (Up to 3 Users)',
        'Advanced Analytics & Reports',
        'Custom Invoice Templates',
        'Priority Email & Chat Support',
        'Automatic Data Backup (Weekly)',
        'Stock Alert Notifications',
        'Barcode/Label Printing',
        'Mobile Dashboard Access',
      ],
      highlight: true,
      ctaText: 'Get Professional',
      recommended: false
    },
    {
      id: 'premium',
      name: 'Enterprise',
      tagline: 'All-in-One Business Suite',
      icon: <DiamondIcon />,
      price: '₹30,000',
      amc: '₹10,000/year',
      description: 'Complete business management with advanced features',
      gradient: gradientStyles.enterprise,
      solidColor: planColors.premium,
      features: [
        'Everything in Professional Plan',
        'Roznama (Daily Business Reports)',
        'Customer & Supplier Ledgers',
        'Multi-branch Support',
        'Unlimited User Access',
        '24/7 Phone & Priority Support',
        'Real-time Data Sync',
        'Advanced Security Features',
        'Custom Module Development',
        'API Access',
        'Yearly Training Sessions',
        'Dedicated Account Manager',
      ],
      highlight: false,
      ctaText: 'Get Enterprise',
      recommended: true
    }
  ];

  // Feature comparison
  const allFeatures = [
    { name: 'Billing Module', basic: true, standard: true, premium: true },
    { name: 'Inventory Module', basic: true, standard: true, premium: true },
    { name: 'Both Modules', basic: false, standard: true, premium: true },
    { name: 'Customer Management', basic: true, standard: true, premium: true },
    { name: 'Supplier Management', basic: false, standard: true, premium: true },
    { name: 'Roznama (Daily Reports)', basic: false, standard: false, premium: true },
    { name: 'Customer Ledger', basic: false, standard: false, premium: true },
    { name: 'Supplier Ledger', basic: false, standard: false, premium: true },
    { name: 'Multi-user Access', basic: '1 User', standard: '3 Users', premium: 'Unlimited' },
    { name: 'Support', basic: 'Email', standard: 'Priority', premium: '24/7 Phone' },
    { name: 'Data Backup', basic: 'Monthly', standard: 'Weekly', premium: 'Real-time' },
    { name: 'Custom Development', basic: false, standard: false, premium: true },
  ];

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    setPaymentDialogOpen(true);
  };

  const handlePaymentSubmit = async () => {
    setPaymentProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      setPaymentProcessing(false);
      setPaymentSuccess(true);
      
      // Close dialog after success
      setTimeout(() => {
        setPaymentDialogOpen(false);
        setPaymentSuccess(false);
        setPaymentDetails({
          name: '',
          email: '',
          phone: '',
          company: '',
          upiId: '',
          transactionId: ''
        });
      }, 10000);
    }, 2000);
  };

  const getPlanById = (id) => plans.find(plan => plan.id === id);

  return (
    <Box sx={{ 
      minHeight: '100vh',
      pt: 5,
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            icon={<CelebrationIcon />}
            label="SPECIAL LAUNCH OFFER"
            sx={{
              background: gradientStyles.primary,
              color: 'white',
              fontWeight: 700,
              fontSize: '0.875rem',
              mb: 3,
              px: 2,
              py: 1
            }}
          />
          
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '2rem', md: '3rem' },
              mb: 2,
              background: gradientStyles.light,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Choose Your Perfect Plan
          </Typography>
          
          <Typography
            // variant="h5"
            sx={{
              color: 'text.secondary',
              maxWidth: '850px',
              mx: 'auto',
              mb: 4,
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            One-time payment with Annual Maintenance Contract. Start with any plan and upgrade anytime.
          </Typography>

          {/* Payment Info Cards */}
          <Grid container spacing={2} sx={{ mb: 6, maxWidth: '1200px', mx: 'auto' }}>
            <Grid item xs={12} md={4} sx={{ maxWidth: '375px' }}>
              <Paper sx={{ 
                p: 3,
                borderRadius: 2,
                ...glassStyles,
                height: '100%'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    p: 1.5,
                    borderRadius: 2,
                    background: gradientStyles.primary,
                    mr: 2
                  }}>
                    <CreditCardIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    One-Time Payment
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Pay once and own the software forever. No monthly subscriptions.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4} sx={{ maxWidth: '375px' }}>
              <Paper sx={{ 
                p: 3,
                borderRadius: 2,
                ...glassStyles,
                height: '100%'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    p: 1.5,
                    borderRadius: 2,
                    background: gradientStyles.premium,
                    mr: 2
                  }}>
                    <SecurityIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    AMC Included
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Annual Maintenance Contract covers updates, support, and bug fixes.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4} sx={{ maxWidth: '375px' }}>
              <Paper sx={{ 
                p: 3,
                borderRadius: 2,
                ...glassStyles,
                height: '100%'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    p: 1.5,
                    borderRadius: 2,
                    background: gradientStyles.enterprise,
                    mr: 2
                  }}>
                    <ChartIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Lifetime Updates
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  All future updates included with active AMC. Stay current with latest features.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Pricing Plans */}
<Grid container spacing={3} sx={{ mb: 10, alignItems: 'stretch' }}>
  {plans.map((plan) => (
    <Grid item xs={12} md={4} key={plan.id} sx={{ 
      display: 'flex', // Make grid item flex container
      flexDirection: 'column',
      maxWidth: '375px'
    }}>
      <Card sx={{
        flex: 1, // Card takes full available height
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'visible',
        borderRadius: 2,
        ...glassStyles,
        transform: plan.highlight ? 'scale(1.02)' : 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: plan.recommended ? `2px solid ${plan.solidColor}` : `1px solid ${alpha(theme.palette.divider, 0.3)}`,
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: isDarkMode
            ? '0 20px 40px rgba(0, 0, 0, 0.3)'
            : '0 20px 40px rgba(37, 99, 235, 0.15)',
        }
      }}>
        {/* Recommended Badge */}
        {plan.recommended && (
          <Box sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: plan.gradient,
            color: 'white',
            px: 3,
            py: 0.5,
            borderRadius: 20,
            fontSize: '0.875rem',
            fontWeight: 700,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <StarIcon sx={{ fontSize: 16 }} />
            MOST POPULAR
          </Box>
        )}

        <CardContent sx={{ 
          p: 4, 
          pt: plan.recommended ? 5 : 4,
          flex: 1, // Takes available space, pushing button to bottom
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Plan Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: plan.gradient,
              mr: 2
            }}>
              {React.cloneElement(plan.icon, { sx: { color: 'white', fontSize: 28 } })}
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                {plan.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {plan.tagline}
              </Typography>
            </Box>
          </Box>

          {/* Price */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h2" sx={{ 
              fontWeight: 900,
              background: plan.gradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}>
              {plan.price}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label="One-Time"
                size="small"
                sx={{
                  background: alpha('#059669', 0.15),
                  color: '#059669',
                  fontWeight: 600
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                + AMC: {plan.amc}
              </Typography>
            </Box>
          </Box>

          {/* Description */}
          <Typography variant="body1" sx={{ 
            color: 'text.primary',
            mb: 3,
            minHeight: '48px'
          }}>
            {plan.description}
          </Typography>

          {/* Features List - This container will grow */}
          <Box sx={{ flex: 1, mt: 'auto' }}>
            <List dense>
              {plan.features.map((feature, index) => (
                <ListItem key={index} sx={{ px: 0, py: 0.75 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckIcon sx={{ 
                      color: plan.solidColor
                    }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={feature}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { color: 'text.primary' }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </CardContent>

        {/* CTA Button - Positioned at bottom */}
        <CardActions sx={{ 
          p: 3, 
          pt: 0,
          mt: 'auto' // This pushes button to bottom
        }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => handlePlanSelect(plan.id)}
            sx={{
              background: plan.gradient,
              borderRadius: 1,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 700,
              '&:hover': {
                background: plan.gradient,
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {plan.ctaText}
          </Button>
        </CardActions>
      </Card>
    </Grid>
  ))}
</Grid>

        {/* Feature Comparison Table */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 800,
              mb: 6,
              background: gradientStyles.light,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Compare All Features
          </Typography>

          <Paper sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            ...glassStyles
          }}>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{
                      padding: '20px',
                      textAlign: 'left',
                      background: alpha(theme.palette.background.paper, 0.5),
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Features
                      </Typography>
                    </th>
                    {plans.map((plan) => (
                      <th key={plan.id} style={{
                        padding: '20px',
                        textAlign: 'center',
                        background: plan.recommended ? alpha(plan.solidColor, 0.1) : alpha(theme.palette.background.paper, 0.5),
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {plan.name}
                        </Typography>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allFeatures.map((feature, index) => (
                    <tr key={feature.name}>
                      <td style={{
                        padding: '16px 20px',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        background: index % 2 === 0 ? alpha(theme.palette.background.paper, 0.3) : 'transparent'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {feature.name}
                        </Typography>
                      </td>
                      {plans.map((plan) => (
                        <td key={`${plan.id}-${feature.name}`} style={{
                          padding: '16px 20px',
                          textAlign: 'center',
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          background: index % 2 === 0 ? alpha(theme.palette.background.paper, 0.3) : 'transparent'
                        }}>
                          {typeof feature[plan.id] === 'boolean' ? (
                            feature[plan.id] ? (
                              <CheckIcon sx={{ color: '#059669' }} />
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                ✗
                              </Typography>
                            )
                          ) : (
                            <Chip
                              label={feature[plan.id]}
                              size="small"
                              sx={{
                                background: alpha(plan.solidColor, 0.1), // Using solid color
                                color: plan.solidColor,
                                fontWeight: 600
                              }}
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Paper>
        </Box>

       {/* FAQ Section */}
<Box sx={{ mb: 10 }}>
  <Typography
    variant="h3"
    sx={{
      textAlign: 'center',
      fontWeight: 800,
      mb: 6,
      background: gradientStyles.light,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    }}
  >
    Frequently Asked Questions
  </Typography>

  {/* Container with fixed max width */}
  <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
    <Paper sx={{
      ...glassStyles,
      p: 3,
      borderRadius: 2
    }}>
      {[
        {
          q: "What does one-time payment include?",
          a: "One-time payment gives you lifetime ownership of the software version you purchase. This includes installation, setup, and basic training."
        },
        {
          q: "What is Annual Maintenance Contract (AMC)?",
          a: "AMC includes software updates, bug fixes, technical support, and security patches. It's required for continued support and is charged annually."
        },
        {
          q: "Can I upgrade my plan later?",
          a: "Yes! You can upgrade from any plan to a higher plan at any time. You'll only pay the difference in one-time cost and pro-rated AMC."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept UPI, Bank Transfer, Credit/Debit Cards, and Cheque payments. GST invoice will be provided for all payments."
        },
        {
          q: "Is there a free trial available?",
          a: "Yes! We offer a 7-day free trial of the Professional plan. Contact our sales team to set up a demo."
        },
        {
          q: "What happens if I don't renew AMC?",
          a: "Your software will continue to work, but you won't receive updates, support, or security patches. AMC can be renewed later with a reinstatement fee."
        }
      ].map((faq, index) => (
        <Accordion
          key={index}
          sx={{
            background: 'transparent',
            boxShadow: 'none',
            mb: 2,
            '&:before': {
              display: 'none'
            },
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            width: '100%', // Ensure full width within container
            '&:hover': {
              borderColor: alpha(theme.palette.primary.main, 0.3),
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[2]
            }
          }}
        >
            <AccordionSummary
              expandIcon={
                <Box sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: gradientStyles.primary,
                  transition: 'transform 0.3s ease'
                }}>
                  <ExpandMoreIcon sx={{ 
                    color: 'white',
                    fontSize: 18,
                    transition: 'transform 0.3s ease'
                  }} />
                </Box>
              }
              sx={{
                px: 3,
                py: 2,
                '&.Mui-expanded': {
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                }
              }}
            >
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                fontSize: '1.05rem'
              }}>
                {faq.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              <Typography variant="body1" sx={{ 
                color: 'text.secondary',
                lineHeight: 1.6,
                pl: 0.5
              }}>
                {faq.a}
              </Typography>
            </AccordionDetails>
          </Accordion>
    ))}
    </Paper>
  </Container>
</Box>

       {/* Contact CTA */}
<Box sx={{ position: 'relative' }}>
  {/* Background decorative elements */}
  <Box sx={{
    position: 'absolute',
    top: -100,
    right: -100,
    width: 300,
    height: 300,
    borderRadius: '50%',
    background: isDarkMode 
      ? 'radial-gradient(circle, rgba(37, 99, 235, 0.1) 0%, transparent 70%)'
      : 'radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, transparent 70%)',
    zIndex: 0
  }} />
  <Box sx={{
    position: 'absolute',
    bottom: -80,
    left: -80,
    width: 250,
    height: 250,
    borderRadius: '50%',
    background: isDarkMode
      ? 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)'
      : 'radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%)',
    zIndex: 0
  }} />

  <Paper sx={{
    pt: 4,
    pb: 4,
    borderRadius: 4,
    textAlign: 'center',
    background: isDarkMode
      ? `linear-gradient(135deg, ${gradientStyles.primary} 0%, ${gradientStyles.premium} 100%)`
      : `linear-gradient(135deg,rgb(190, 203, 232) 0%,rgb(134, 128, 241) 100%)`,
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: isDarkMode
      ? '0 20px 60px rgba(37, 99, 235, 0.25)'
      : '0 20px 60px rgba(37, 99, 235, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: isDarkMode
        ? 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 60%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 60%)'
        : 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.2) 0%, transparent 60%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 60%)',
      zIndex: 1
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
      animation: 'shimmer 3s infinite',
      '@keyframes shimmer': {
        '0%': { transform: 'translateX(-100%)' },
        '100%': { transform: 'translateX(100%)' }
      }
    }
  }}>
    <Box sx={{ position: 'relative', zIndex: 2 }}>
      {/* Icon/Decoration */}
      <Box sx={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: isDarkMode
          ? 'rgba(255, 255, 255, 0.15)'
          : 'rgba(255, 255, 255, 0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
        border: isDarkMode
          ? '2px solid rgba(255, 255, 255, 0.3)'
          : '2px solid rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(10px)',
        boxShadow: isDarkMode
          ? '0 8px 32px rgba(0, 0, 0, 0.2)'
          : '0 8px 32px rgba(0, 0, 0, 0.1)',
        animation: 'float 6s ease-in-out infinite',
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-10px)' }
        }
      }}>
        <Box component="img" 
          src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/%3E%3C/svg%3E" 
          alt="Support"
          sx={{ opacity: isDarkMode ? 0.9 : 0.95 }}
        />
      </Box>

      {/* Title */}
      <Typography variant="h2" sx={{ 
        fontWeight: 900, 
        mb: 2,
        fontSize: { xs: '2rem', md: '2.75rem' },
        textShadow: isDarkMode
          ? '0 2px 10px rgba(0, 0, 0, 0.2)'
          : '0 2px 8px rgba(0, 0, 0, 0.15)'
      }}>
        Need Custom Solution?
      </Typography>
      
      {/* Subtitle */}
      <Typography variant="h6" sx={{ 
        mb: 5, 
        opacity: isDarkMode ? 0.9 : 0.95,
        maxWidth: '600px',
        margin: '0 auto',
        fontSize: { xs: '1rem', md: '1.25rem' },
        fontWeight: 400,
        lineHeight: 1.6
      }}>
        Let's build exactly what you need. Contact our sales team for enterprise solutions, 
        custom features, or personalized requirements.
      </Typography>

      {/* Contact Methods */}
      <Box sx={{ 
        display: 'flex', 
        gap: 3, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        mb: 4,
        mt: 4
      }}>
        {/* Phone Card */}
        <Paper sx={{
          p: 3,
          borderRadius: 3,
          background: isDarkMode
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(10px)',
          border: isDarkMode
            ? '1px solid rgba(255, 255, 255, 0.2)'
            : '1px solid rgba(255, 255, 255, 0.25)',
          minWidth: { xs: '100%', sm: '280px' },
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            background: isDarkMode
              ? 'rgba(255, 255, 255, 0.15)'
              : 'rgba(255, 255, 255, 0.2)',
            boxShadow: isDarkMode
              ? '0 10px 30px rgba(0, 0, 0, 0.2)'
              : '0 10px 30px rgba(0, 0, 0, 0.15)'
          }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            mb: 2 
          }}>
            <Box sx={{
              width: 50,
              height: 50,
              borderRadius: '12px',
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.15) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: isDarkMode
                ? '1px solid rgba(255, 255, 255, 0.3)'
                : '1px solid rgba(255, 255, 255, 0.35)'
            }}>
              <PhoneIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                opacity: isDarkMode ? 0.8 : 0.9,
                fontSize: '0.875rem'
              }}>
                Call Us
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                +91 98765 43210
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Email Card */}
        <Paper sx={{
          p: 3,
          borderRadius: 3,
          background: isDarkMode
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(10px)',
          border: isDarkMode
            ? '1px solid rgba(255, 255, 255, 0.2)'
            : '1px solid rgba(255, 255, 255, 0.25)',
          minWidth: { xs: '100%', sm: '280px' },
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            background: isDarkMode
              ? 'rgba(255, 255, 255, 0.15)'
              : 'rgba(255, 255, 255, 0.2)',
            boxShadow: isDarkMode
              ? '0 10px 30px rgba(0, 0, 0, 0.2)'
              : '0 10px 30px rgba(0, 0, 0, 0.15)'
          }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            mb: 2 
          }}>
            <Box sx={{
              width: 50,
              height: 50,
              borderRadius: '12px',
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.15) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: isDarkMode
                ? '1px solid rgba(255, 255, 255, 0.3)'
                : '1px solid rgba(255, 255, 255, 0.35)'
            }}>
              <EmailIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                opacity: isDarkMode ? 0.8 : 0.9,
                fontSize: '0.875rem'
              }}>
                Email Us
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                jdarshan535@gmail.com
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Additional Info */}
      <Box sx={{ 
        mt: 4,
        p: 3,
        borderRadius: 3,
        background: isDarkMode
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(255, 255, 255, 0.1)',
        border: isDarkMode
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(255, 255, 255, 0.15)',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        <Typography variant="body2" sx={{ 
          opacity: isDarkMode ? 0.8 : 0.9,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          fontSize: '0.9rem',
          color: 'black'
        }}>
          <AccessTimeIcon fontSize="small" />
          Available Mon-Sat, 9:00 AM - 7:00 PM IST
        </Typography>
      </Box>
    </Box>
  </Paper>
</Box>
      </Container>

      {/* Payment Dialog */}
<Dialog
  open={paymentDialogOpen}
  onClose={() => !paymentProcessing && setPaymentDialogOpen(false)}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      background: isDarkMode
        ? 'rgba(30, 41, 59, 0.95)'
        : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(30px)',
      WebkitBackdropFilter: 'blur(30px)',
      border: `1px solid ${isDarkMode ? 'rgba(148, 163, 184, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
      borderRadius: '24px',
      boxShadow: isDarkMode
        ? '0 20px 60px rgba(0, 0, 0, 0.4)'
        : '0 20px 60px rgba(37, 99, 235, 0.15)',
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: selectedPlan ? getPlanById(selectedPlan)?.gradient : gradientStyles.primary,
      }
    }
  }}
>
  {paymentSuccess ? (
    <Box sx={{ p: 6, textAlign: 'center' }}>
      {/* Success Animation */}
      <Box sx={{ 
        position: 'relative',
        width: 100,
        height: 100,
        mx: 'auto',
        mb: 4
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: gradientStyles.enterprise,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': { transform: 'scale(1)', opacity: 1 },
            '50%': { transform: 'scale(1.05)', opacity: 0.8 },
            '100%': { transform: 'scale(1)', opacity: 1 }
          }
        }}>
          <CheckIcon sx={{ color: 'white', fontSize: 48 }} />
        </Box>
        <Box sx={{
          position: 'absolute',
          top: -10,
          left: -10,
          right: -10,
          bottom: -10,
          border: `2px solid ${gradientStyles.enterprise}`,
          borderRadius: '50%',
          opacity: 0.3,
          animation: 'ripple 2s infinite',
          '@keyframes ripple': {
            '0%': { transform: 'scale(0.8)', opacity: 0.3 },
            '100%': { transform: 'scale(1.5)', opacity: 0 }
          }
        }} />
      </Box>

      <Typography variant="h3" sx={{ 
        fontWeight: 900, 
        mb: 2,
        background: gradientStyles.enterprise,
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}>
        Payment Successful!
      </Typography>
      
      <Typography variant="body1" sx={{ 
        color: 'text.secondary',
        mb: 4,
        fontSize: '1.1rem',
        lineHeight: 1.6
      }}>
        Thank you for your purchase! Our team will contact you within 24 hours for software setup and activation.
      </Typography>

      <Box sx={{ 
        p: 3, 
        mb: 4,
        borderRadius: 3,
        background: alpha('#059669', 0.1),
        border: `1px solid ${alpha('#059669', 0.2)}`
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
          Next Steps:
        </Typography>
        <Box component="ul" sx={{ pl: 2, m: 0, color: 'text.secondary' }}>
          <li>Check your email for payment confirmation</li>
          <li>Our support team will contact you shortly</li>
          <li>Prepare for installation requirements</li>
        </Box>
      </Box>

      <Button
        variant="contained"
        size="large"
        onClick={() => {
          setPaymentDialogOpen(false);
          setPaymentSuccess(false);
        }}
        sx={{
          background: gradientStyles.primary,
          borderRadius: '12px',
          px: 6,
          py: 1.5,
          fontSize: '1rem',
          fontWeight: 700,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(37, 99, 235, 0.3)'
          },
          transition: 'all 0.3s ease'
        }}
      >
        Close & Continue
      </Button>
    </Box>
  ) : (
    <>
      {/* Header */}
      <Box sx={{ 
        p: 4, 
        pb: 3,
        background: selectedPlan ? alpha(getPlanById(selectedPlan)?.solidColor, 0.05) : alpha(theme.palette.primary.main, 0.05),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary' }}>
            Complete Purchase
          </Typography>
          <IconButton
            size="small"
            onClick={() => !paymentProcessing && setPaymentDialogOpen(false)}
            disabled={paymentProcessing}
            sx={{
              borderRadius: '10px',
              background: alpha(theme.palette.text.primary, 0.1),
              '&:hover': {
                background: alpha(theme.palette.text.primary, 0.2)
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {selectedPlan && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <Chip
              icon={getPlanById(selectedPlan)?.icon}
              label={getPlanById(selectedPlan)?.name}
              sx={{
                background: getPlanById(selectedPlan)?.gradient,
                color: 'white',
                fontWeight: 700,
                fontSize: '0.9rem',
                height: 32,
                '& .MuiChip-icon': {
                  color: 'white',
                  fontSize: 18
                }
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h4" sx={{ 
                fontWeight: 900,
                background: getPlanById(selectedPlan)?.gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                {getPlanById(selectedPlan)?.price}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                + AMC: {getPlanById(selectedPlan)?.amc}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <DialogContent sx={{ p: 4, pt: 3 }}>
        {/* Progress Steps */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
            Complete these 3 steps:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {['1', '2', '3'].map((step, index) => (
              <React.Fragment key={step}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: index === 0 ? gradientStyles.primary : alpha(theme.palette.divider, 0.3),
                  color: index === 0 ? 'white' : 'text.secondary',
                  fontWeight: 700,
                  fontSize: '0.9rem'
                }}>
                  {step}
                </Box>
                {index < 2 && (
                  <Box sx={{
                    flex: 1,
                    height: 2,
                    background: alpha(theme.palette.divider, 0.3),
                    borderRadius: 1
                  }} />
                )}
              </React.Fragment>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Fill Details
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Make Payment
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Get Access
            </Typography>
          </Box>
        </Box>

        {/* Form */}
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Full Name"
              value={paymentDetails.name}
              onChange={(e) => setPaymentDetails({...paymentDetails, name: e.target.value})}
              required
              variant="outlined"
              size="medium"
              sx={{ mb: 2 }}
              InputProps={{
                sx: {
                  borderRadius: '12px',
                  background: alpha(theme.palette.background.paper, 0.5)
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Company Name (Optional)"
              value={paymentDetails.company}
              onChange={(e) => setPaymentDetails({...paymentDetails, company: e.target.value})}
              variant="outlined"
              size="medium"
              sx={{ mb: 2 }}
              InputProps={{
                sx: {
                  borderRadius: '12px',
                  background: alpha(theme.palette.background.paper, 0.5)
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={paymentDetails.email}
              onChange={(e) => setPaymentDetails({...paymentDetails, email: e.target.value})}
              required
              variant="outlined"
              size="medium"
              sx={{ mb: 2 }}
              InputProps={{
                sx: {
                  borderRadius: '12px',
                  background: alpha(theme.palette.background.paper, 0.5)
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={paymentDetails.phone}
              onChange={(e) => setPaymentDetails({...paymentDetails, phone: e.target.value})}
              required
              variant="outlined"
              size="medium"
              sx={{ mb: 2 }}
              InputProps={{
                sx: {
                  borderRadius: '12px',
                  background: alpha(theme.palette.background.paper, 0.5)
                }
              }}
            />
          </Grid>
        </Grid>

        {/* Payment Section */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
            Payment Information
          </Typography>
          
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Method"
                value={paymentDetails.upiId}
                onChange={(e) => setPaymentDetails({...paymentDetails, upiId: e.target.value})}
                required
                placeholder="UPI ID, Bank Transfer, etc."
                variant="outlined"
                size="medium"
                sx={{ mb: 2 }}
                InputProps={{
                  sx: {
                    borderRadius: '12px',
                    background: alpha(theme.palette.background.paper, 0.5)
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Transaction ID / Reference Number"
                value={paymentDetails.transactionId}
                onChange={(e) => setPaymentDetails({...paymentDetails, transactionId: e.target.value})}
                required
                placeholder="Enter transaction reference"
                variant="outlined"
                size="medium"
                sx={{ mb: 2 }}
                InputProps={{
                  sx: {
                    borderRadius: '12px',
                    background: alpha(theme.palette.background.paper, 0.5)
                  }
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Payment Instructions */}
        <Paper sx={{ 
          p: 3, 
          mb: 3,
          borderRadius: '16px',
          background: `linear-gradient(135deg, ${alpha('#059669', 0.1)} 0%, ${alpha('#2563EB', 0.1)} 100%)`,
          border: `1px solid ${alpha('#059669', 0.2)}`,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            position: 'absolute', 
            top: -20, 
            right: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: alpha('#059669', 0.1)
          }} />
          
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 700, 
            mb: 2, 
            color: 'success.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <InfoIcon fontSize="small" />
            Payment Instructions
          </Typography>
          
          <Box component="ul" sx={{ 
            pl: 2, 
            m: 0, 
            color: 'text.secondary',
            '& li': {
              mb: 1,
              '&:last-child': { mb: 0 }
            }
          }}>
            {/* <li><strong>UPI:</strong> jewelstock@hdfcbank</li> */}
            {/* <li><strong>Bank:</strong> HDFC Bank • A/C: 501002345678 • IFSC: HDFC0000123</li> */}
            {/* <li><strong>Email receipt:</strong> payments@jewelstock.com</li> */}
            <li><strong>We will shortly start online services</strong></li>
          </Box>
        </Paper>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ 
        p: 4, 
        pt: 2,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        background: alpha(theme.palette.background.paper, 0.5)
      }}>
        <Button
          onClick={() => setPaymentDialogOpen(false)}
          disabled={paymentProcessing}
          sx={{ 
            color: 'text.secondary',
            borderRadius: '12px',
            px: 4,
            py: 1,
            '&:hover': {
              background: alpha(theme.palette.text.secondary, 0.1)
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePaymentSubmit}
          disabled={paymentProcessing || !paymentDetails.name || !paymentDetails.email || !paymentDetails.transactionId}
          startIcon={paymentProcessing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
          sx={{
            background: selectedPlan ? getPlanById(selectedPlan)?.gradient : gradientStyles.primary,
            borderRadius: '12px',
            px: 5,
            py: 1.2,
            fontSize: '1rem',
            fontWeight: 700,
            minWidth: '180px',
            '&:hover': {
              background: selectedPlan ? getPlanById(selectedPlan)?.gradient : gradientStyles.primary,
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)'
            },
            '&:disabled': {
              background: alpha(theme.palette.text.disabled, 0.3),
              color: alpha(theme.palette.text.disabled, 0.5)
            },
            transition: 'all 0.3s ease'
          }}
        >
          {paymentProcessing ? 'Processing...' : 'Complete Purchase'}
        </Button>
      </DialogActions>
    </>
  )}
</Dialog>
    </Box>
  );
};

export default SubscriptionPage;